import { gameProviderTypes, selectedAppInfoKey } from '@configs/layout';
import { layoutActions } from '@reducers/layout/layoutSlice';
import { useState, useCallback, Suspense, ChangeEvent, useLayoutEffect, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@reducers/index';
import SearchInputPC from '@components/template/layout/header/searchGameInput/pc/searchInputPC';
import SearchInputMobile from '@components/template/layout/header/searchGameInput/mobile/searchInputMobile';
import { debounce } from 'lodash';
import OutsideClickHandler from 'react-outside-click-handler';
import * as layoutThunk from '@reducers/layout/layoutThunk';
import { useLocation, useNavigate } from 'react-router-dom';
import { getLanguageCode } from '@utils/common/language';

import { axiosDummyInstance, axiosScraper } from '@utils/axios/axiosInstance';
import MyAccountPopup from './myAccountPopup';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const { selectedAppInfo, selectedCountry, selectedLanguage, isSelectedApp, myAppObj } = useSelector(
    ({ LAYOUT }: RootState) => LAYOUT
  );

  const [searchResultList, setSearchResultList] = useState([]);
  const [isMobileSearch, setIsMobileSearch] = useState(false);
  const [myAccountPopupFlag, setMyAccountPopupFlag] = useState(false);
  const [showResult, setShowResult] = useState<boolean>(false);

  const [searchText, setSearchText] = useState('');

  const [showSearchBar, setShowSearchBar] = useState(false);

  // 검색창 구분처리
  useEffect(() => {
    if (location && location?.pathname) {
      const pathName = location.pathname.split('/').length > 0 ? location.pathname.split('/')[1] : '';
      if (pathName === 'app') {
        setShowSearchBar(true);
      } else {
        setShowSearchBar(false);
      }
    }
  }, [location]);

  // header쪽 searchText 값 넣는 useEffect
  useEffect(() => {
    if (isSelectedApp && !!selectedAppInfo) {
      setSearchText(selectedAppInfo?.title);
    } else {
      // setSearchResultList([]);
      setSearchText('');
    }
  }, [isSelectedApp]);

  /**
   * keyword fetch event
   */
  const handleInputKeyEvent = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      setSearchText(input);
      handleinputDebounce(input, selectedCountry);
    },
    [selectedCountry]
  );

  /**
   * debounce keyword fetch
   */
  const handleinputDebounce = useCallback(
    debounce((newSearchText, selectedCountry) => {
      // 국가만 보내기 언어는 국가에 포함된 언어가 나옴..
      axiosScraper
        .post('/axo/headerSearch', {
          term: newSearchText,
          country: selectedCountry.toLowerCase(),
        })
        .then((response) => {
          const { data } = response;
          // const filterData = data.filter((x) => x.title.toUpperCase().includes(newSearchText.toUpperCase()));
          // TODO:
          setSearchResultList(data.map((item) => ({ ...item, providerType: item?.provider_type })));
        });
    }, 1000),
    []
  );

  const handleOnBlur = () => {
    setShowResult(false);
  };

  const handleOnFucus = useCallback(() => {
    setShowResult(true);
  }, []);

  // 모바일 버전에서 input text 삭제 버튼 (버튼 클릭시 searchText, searchResult 초기화)
  const handleSearchDeleteButtonClick = useCallback(() => {
    localStorage.removeItem(selectedAppInfoKey);
    dispatch(layoutActions.setHeaderSearchedAppInfo({ data: null, isSelectedApp: false }));
    // 국가 선택 및 언어 선택 기존의 myAppObj 으로 변경하기
    dispatch(layoutActions.setCountryFlag(myAppObj?.country.toUpperCase()));
    dispatch(layoutActions.setLanguage(getLanguageCode(myAppObj?.country.toUpperCase())));
    setSearchResultList([]);
    setSearchText('');
  }, [myAppObj]);

  // App리스트에서 클릭시 App 선택
  const handleSelectApp = useCallback(
    (item) => {
      if (item) {
        const { appId, type, id, developerId, developer, title, icon, genres } = item;
        const appInfoId = { appId, type };
        localStorage.setItem(selectedAppInfoKey, JSON.stringify(appInfoId));
        setSearchText(title);
        // dispatch(
        //   setSelectedAppInfo({
        //     data: {
        //       // appId,
        //       // type,
        //       // id,
        //       // title,
        //       // icon,
        //       // genres,
        //       ...item,
        //       developer_search_id: type === gameProviderTypes.AppleAppStore ? developerId : developer,
        //     },
        //     isSelectedApp: true,
        //   })
        // );

        dispatch(
          layoutThunk.getHeaderSearchedAppInfo({
            provider_type: item?.provider_type,
            appId: item?.appId,
            country: selectedCountry,
            lang: selectedLanguage,
          })
        );
        setSearchResultList([]);
      }
    },
    [dispatch, selectedCountry, selectedCountry]
  );

  const handleLeftMenuCloseClick = useCallback(() => {
    dispatch(layoutActions.leftMenuToggle({ data: true }));
  }, [dispatch]);

  const onClickMyAccountPopupHandler = useCallback(() => {
    setMyAccountPopupFlag((prev) => !prev);
    navigate('/accountSettings/account');
  }, []);

  const myAccountPopupClose = useCallback((event) => {
    //button-check
    const className = event.target.className;
    if (className !== 'button-check' && className !== 'btn-s btn-account') {
      setMyAccountPopupFlag(false);
    } else if (className === 'btn-ico logout') {
      setMyAccountPopupFlag(false);
    }
  }, []);

  const onClickAppManager = useCallback(() => {
    navigate('/appManager/myApps');
  }, [navigate]);

  const onClickAlertSetting = useCallback(() => {
    navigate('/alertSetting');
  }, []);

  return (
    <>
      {showSearchBar && (
        <SearchInputMobile
          searchText={searchText}
          onInputKeyEvent={handleInputKeyEvent}
          handleOnBlur={handleOnBlur}
          handleSearchDeleteButtonClick={handleSearchDeleteButtonClick}
          searchResultList={searchResultList}
          handleSelectApp={handleSelectApp}
          handleOnFucus={handleOnFucus}
          setIsMobileSearch={setIsMobileSearch}
          showResult={showResult}
        />
      )}
      <div className={`header-inner ${!showSearchBar ? 'no_search-box' : ''}`}>
        <div className="header-mo">
          <button type="button" className="btn-side" onClick={handleLeftMenuCloseClick}>
            <span>메뉴보기</span>
          </button>
          <h1 className="header-mlogo">
            <img src="../../images/svg/m-logo-axo.svg" alt="" />
          </h1>
        </div>
        {showSearchBar && (
          <SearchInputPC
            searchText={searchText}
            onInputKeyEvent={handleInputKeyEvent}
            searchResultList={searchResultList}
            handleOnBlur={handleOnBlur}
            handleSearchDeleteButtonClick={handleSearchDeleteButtonClick}
            handleSelectApp={handleSelectApp}
            handleOnFucus={handleOnFucus}
            setIsMobileSearch={setIsMobileSearch}
            showResult={showResult}
          />
        )}
        <div className={`header-account ${myAccountPopupFlag ? 'active' : ''}`}>
          {/* 국기모양 국가선택 버튼 이전버전 */}
          {/* <div className="country_select_wrp">
            <CountryFlagIcon />
          </div> */}
          {/* 다국어 선택 이전 버전 */}
          {/* <LanguageSelect /> */}
          {/* MyAccount 새버전 MyAccount 화면으로 연결시킨다. */}
          <button type="button" className="header-app-manager" onClick={onClickAppManager}>
            <i></i>
            <span>App Manager</span>
          </button>
          {/* MyAccount 버튼 이전 버전 */}
          {/* <button type="button" className="btn-s btn-account" onClick={onClickMyAccountPopupHandler}>
            <span className="button-check">My Account</span>
          </button> */}
          {/* alert 버튼 새버전 - aler 화면으로 연결시킨다 */}
          <button type="button" className="header-alert-manager" onClick={onClickAlertSetting}>
            <i></i>
            <span>Alert Setting</span>
          </button>
          <button type="button" className="header-mypage" onClick={onClickMyAccountPopupHandler}>
            {/* 프로필 이미지 삽입시 노출 */}
            {userInfo?.profileImageUrl && <img src={userInfo?.profileImageUrl} alt="" />}
            {/* <img src="../../images/dummy/sample-3.png" alt="" /> */}
            <span>mypage</span>
          </button>
          <OutsideClickHandler onOutsideClick={myAccountPopupClose}>
            {myAccountPopupFlag && <MyAccountPopup popupCloseFn={myAccountPopupClose} />}
          </OutsideClickHandler>
        </div>
      </div>
    </>
  );
};

export default Header;
